/**
 * Clinical theme preset – navy primary, professional and trustworthy.
 *
 * Designed for medical specialists (orthopedics, dermatology, psychiatry) where
 * trust and clinical authority are the primary signals. Key design decisions:
 * - Navy blue primary communicates credibility
 * - Inter for both body and heading – clean, no-nonsense sans-serif
 * - `cardStyle: border-shadow` – visible card structure reinforces organization
 * - `iconStyle: outlined` – outlined icons feel clinical and precise
 * - `buttonStyle: rounded` – standard corners; more structured than pill,
 *   less austere than square, appropriate for clinical authority
 *
 * AI-NOTE: This preset pairs with `heroStyle: gradient`. When generating pages
 * for clinical providers, prefer gradient heroes over photography-forward heroes.
 */

import { ThemeSchema, type Theme } from '../types';
import { DEFAULT_DENSITY } from './base';

export const clinicalTheme: Theme = ThemeSchema.parse({
  name: 'clinical',
  colors: {
    // Primary: Navy blue - trustworthy, clinical
    primary: '#1E40AF',
    primaryDark: '#1E3A8A',
    primaryLight: '#3B82F6',
    // Accent: Teal - secondary actions, wellness (darkened for AA contrast)
    accent: '#0A766C',
    accentDark: '#075953',
    accentLight: '#14B8A6',
    // Surfaces
    background: '#FFFFFF',
    backgroundAlt: '#F8FAFC',
    surface: '#FFFFFF',
    // Text
    text: '#1E293B',
    textMuted: '#64748B',
    textInverse: '#FFFFFF',
    // Borders
    border: '#E2E8F0',
    // Status
    success: '#059669',
    warning: '#D97706',
    error: '#DC2626',
    // Medical-specific
    // HAL-33: these were verbatim copies of `success` / `warning` above, which
    // is why they inherited ratios sized for the wrong job. Status tokens paint
    // icons and badges and owe 3:1 (WCAG 1.4.11) -- #059669 and #D97706 clear
    // that and are left alone. Evidence tokens render as a 12px h4 in
    // GroupedSourceList, so they are normal TEXT and owe 4.5:1, where the same
    // two values measured 3.77 and 3.19. Decoupled and darkened one stop down
    // the same ramps: #047857 is 5.48:1, #B45309 is 5.02:1.
    //
    // This is forced, not elective. `contrast.spec.ts` iterates all nine
    // presets, so adding the evidence trio to NORMAL_TEXT_PAIRS -- the guard
    // HAL-33 needed against the `base` token regressing -- turns clinical red
    // on the same run. The choice was "extend the contract and fix clinical"
    // or "leave the contract unextended", and the second is what let #0E8A5F
    // sit at 4.36:1 unnoticed for months. That no provider ships `clinical`
    // today is why this costs no rebuild, not why it was done.
    evidenceStrong: '#047857',
    evidenceModerate: '#B45309',
    evidenceLow: '#6B7280',
    safetyWarning: '#DC2626',
    safetyInfo: '#2563EB',
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
    fontFamilyHeading: '"Inter", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
    fontWeightHeading: '700',
    fontSizeBase: '1rem',
    fontSizeSm: '0.875rem',
    fontSizeLg: '1.125rem',
    fontSizeXl: '1.25rem',
    fontSize2xl: '1.5rem',
    fontSize3xl: '1.875rem',
    fontSize4xl: '2.25rem',
    lineHeightBase: '1.75',
    lineHeightTight: '1.3',
    lineHeightLoose: '1.8',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
  },
  borderRadius: {
    none: '0',
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(30 64 175 / 0.05)',
    md: '0 4px 6px -1px rgb(30 64 175 / 0.1), 0 2px 4px -2px rgb(30 64 175 / 0.1)',
    lg: '0 10px 15px -3px rgb(30 64 175 / 0.1), 0 4px 6px -4px rgb(30 64 175 / 0.1)',
    xl: '0 20px 25px -5px rgb(30 64 175 / 0.1), 0 8px 10px -6px rgb(30 64 175 / 0.1)',
  },
  layout: {
    heroStyle: 'gradient',
    cardStyle: 'border-shadow',
    iconStyle: 'outlined',
    buttonStyle: 'rounded',
    sectionPadding: '4rem',
    contentMaxWidth: '65ch',
  },
  // HAL-495: shared layout rhythm + ch-based reading measures. See DEFAULT_DENSITY.
  density: DEFAULT_DENSITY,
});
